import { latSchema, lngSchema } from "@/lib/crg_apis/types/latlng";
import { z } from "zod";

// silly me; can't seem to figure this out :'(
// import { getConfig } from "@/features/branding";
// const constraints = getConfig().inputConstraints;

const constraints = {
  // NB this is a duplicate of the theme base; see silly comment above
  addressLength: {
    min: 1,
    max: 200
  },
  buildYear: {
    min: 1600,
    max: 2100
  },
  floorHeight: {
    min: 0,
    max: 10000
  }
};

// leave the " (optional, blah blah blah)" off fields in the errors list
const trimAdornment = (f: string | number) => f.toString().replace(/ \(.+/, "");

/** Parses an enum ZodError and generates good error messages. */
export const enumErrorMap: z.ZodErrorMap = (error, ctx) => {
  const field = trimAdornment(error.path[0]);
  switch (error.code) {
    case z.ZodIssueCode.invalid_type:
      {
        return {
          message: `Please provide a value for required column "${field}".`
        };
      }
    case z.ZodIssueCode.invalid_enum_value:
      {
        const opts = error.options.join("', '");
        return {
          message: `Incorrect value "${error.received}" given for "${field}" - correct values are: "${opts}."`
        };
      }
  }
  return {
    message: ctx.defaultError
  };
};

/** This list is comprehensive, for tests and such. */
const archetypes = ["House", "Townhouse", "Apartment", "High-rise Residential Flat", "Low-rise Residential Flat", "Freestanding House", "Shops/Shopping Malls", "Commercial Skyscraper", "Commercial Building", "Simple House", "High Rise Flat", "Low Rise Flat", "Simple Office", "Low Rise Structure", "High Rise Structure", "Supermarket", "Control Room", "Data Center", "Substation", "Wind Turbine", "Park Complex", "Council Building", "Maximum Vulnerability Archetype", "Generic Industrial", "Track Main", "Gas Distribution", "Overhead Power Pole", "Berth", "Aerated Sewage Treatment Pond", "Oxidation Sewage Treatment Pond", "Shiploader",
// HKMA Archetypes
"High Rise Residential Flat", "Low Rise Residential Flat", "Freestanding House HK", "High Rise Structure Commercial", "Low Rise Structure Commercial", "Supermarket Shopping Mall"] as const;
export const keys = {
  id: "user_supplied_id (optional)",
  address: "address (Required if no coordinates supplied)",
  latitude: "latitude (Required if no address supplied)",
  longitude: "longitude (Required if no address supplied)",
  elevation: "elevation (metres, optional)",
  archetype: "archetype (Required)",
  buildYear: "build_year (optional)",
  floorHeight: "[Floor] height_above_ground (metres, optional)",
  marketValue: "market_value (optional)",
  replacementCost: "replacement_cost (optional)",
  mortgageRate: "mortgage_rate (decimal, optional)",
  heatThresholdReturnPeriod: "[Extreme] heat.thresholds.return_period (number, optional)",
  heatThresholdReturnFrequency: "[Extreme] heat.thresholds.return_frequency (decimal, optional)",
  heatThresholdTemperature: "[Extreme] heat.thresholds.temperature (degrees celsius, optional)",
  windThresholdReturnPeriod: "[Extreme] wind.thresholds.return_period (number, optional)",
  windThresholdReturnFrequency: "[Extreme] wind.thresholds.return_frequency (decimal, optional)",
  tcwThresholdReturnPeriod: "[Tropical] cyclone_wind.thresholds.return_period (number, optional)",
  tcwThresholdReturnFrequency: "[Tropical] cyclone_wind.thresholds.return_frequency (decimal, optional)",
  tcwThresholdWindSpeed: "[Tropical] cyclone_wind.thresholds.wind_speed (number, optional)",
  fireProtection: "fire_protection (optional)",
  foundationDesign: "foundation_design (optional)"
} as const;

// for backward compatibility, TODO: if needed, could change string to array to allocate for multiple changes
export const outdatedKeys = {
  heatThresholdTemperature: "[Extreme Heat Failure] thresholds.heat.temperature (degrees celsius, optional)",
  heatThresholdReturnPeriod: "[Extreme Heat Failure] thresholds.heat.rp (number, optional)",
  windThresholdReturnPeriod: "[Extreme Wind] thresholds.wind.rp (number, optional)",
  tcwThresholdWindSpeed: "[Tropical Cyclone Wind] thresholds.cyclone_wind.wind_speed (number, optional)",
  tcwThresholdReturnPeriod: "[Tropical Cyclone Wind] thresholds.cyclone_wind.rp (number, optional)"
} as const;
export const rawSchemaBase = z.object({
  [keys.id]: z.union([z.string(), z.number()]).nullish().optional(),
  [keys.latitude]: z.number().nullish().optional(),
  [keys.longitude]: z.number().nullish().optional(),
  [keys.elevation]: z.number().nullish().optional(),
  [keys.archetype]: z.enum(archetypes, {
    errorMap: enumErrorMap
  }).nullish().optional(),
  [keys.buildYear]: z.number({
    errorMap: () => ({
      message: `Build Year must be between ${constraints.buildYear.min} and ${constraints.buildYear.max} (inclusive).`
    })
  }).gte(constraints.buildYear.min).lte(constraints.buildYear.max).nullish().optional(),
  [keys.floorHeight]: z.number({
    errorMap: () => ({
      message: `Height Above Ground must be between ${constraints.floorHeight.min} and ${constraints.floorHeight.max} (inclusive).`
    })
  }).gte(constraints.floorHeight.min).lte(constraints.floorHeight.max).nullish().optional(),
  [keys.marketValue]: z.number({
    errorMap: () => ({
      message: `Market value must be greater than 0.`
    })
  }).gt(0).nullish().optional(),
  [keys.replacementCost]: z.number({
    errorMap: () => ({
      message: `Replacement cost must be greater than 0.`
    })
  }).gt(0).nullish().optional(),
  [keys.mortgageRate]: z.number({
    errorMap: () => ({
      message: `Mortgage Rate must be greater than 0.`
    })
  }).gt(0).nullish().optional(),
  [keys.heatThresholdReturnPeriod]: z.number({
    errorMap: () => ({
      message: `Heat Failure Threshold return period must be greater than 1.`
    })
  }).gt(1).nullish().optional(),
  [outdatedKeys.heatThresholdReturnPeriod]: z.number({
    errorMap: () => ({
      message: `Heat Failure Threshold return period must be greater than 1.`
    })
  }).gt(1).nullish().optional(),
  [keys.heatThresholdReturnFrequency]: z.number({
    errorMap: () => ({
      message: `Heat Failure Threshold return frequency must be greater than 0 and less than 1.`
    })
  }).gt(0).lt(1).nullish().optional(),
  [keys.heatThresholdTemperature]: z.number({
    errorMap: () => ({
      message: `Heat Failure Threshold temperature must be within the 26-52 range.`
    })
  }).gte(26).lte(52).nullish().optional(),
  [outdatedKeys.heatThresholdTemperature]: z.number({
    errorMap: () => ({
      message: `Heat Failure Threshold temperature must be within the 26-52 range.`
    })
  }).gte(26).lte(52).nullish().optional(),
  [keys.windThresholdReturnPeriod]: z.number({
    errorMap: () => ({
      message: `Extreme Wind Threshold return period must be greater than 1.`
    })
  }).gt(1).nullish().optional(),
  [outdatedKeys.windThresholdReturnPeriod]: z.number({
    errorMap: () => ({
      message: `Extreme Wind Threshold return period must be greater than 1.`
    })
  }).gt(1).nullish().optional(),
  [keys.windThresholdReturnFrequency]: z.number({
    errorMap: () => ({
      message: `Extreme Wind Threshold return frequency must be greater than 0 and less than 1.`
    })
  }).gt(0).lt(1).nullish().optional(),
  [keys.tcwThresholdReturnPeriod]: z.number({
    errorMap: () => ({
      message: `Tropical Cyclone Wind threshold return period must be greater than 1.`
    })
  }).gt(1).nullish().optional(),
  [outdatedKeys.tcwThresholdReturnPeriod]: z.number({
    errorMap: () => ({
      message: `Tropical Cyclone Wind threshold return period must be greater than 1.`
    })
  }).gt(1).nullish().optional(),
  [keys.tcwThresholdReturnFrequency]: z.number({
    errorMap: () => ({
      message: `Tropical Cyclone Wind threshold return frequency must be greater than 0 and less than 1.`
    })
  }).gt(0).lt(1).nullish().optional(),
  [keys.tcwThresholdWindSpeed]: z.number({
    errorMap: () => ({
      message: `Tropical Cyclone Wind threshold speed must be greater than 0 and less than 120.`
    })
  }).gt(0).lt(120).nullish().optional(),
  [outdatedKeys.tcwThresholdWindSpeed]: z.number({
    errorMap: () => ({
      message: `Tropical Cyclone Wind threshold speed must be greater than 0 and less than 120.`
    })
  }).gt(0).lt(120).nullish().optional(),
  [keys.fireProtection]: z.enum(["none", "heatEmber", "flame"], {
    errorMap: () => ({
      message: `Fire Protection must be one of: none, heatEmber, flame.`
    })
  }).nullish().optional(),
  [keys.foundationDesign]: z.enum(["normal", "rigid"], {
    errorMap: () => ({
      message: `Foundation Design must be one of: normal, rigid.`
    })
  }).nullish().optional()
});
const rawSchemaLocationWithAddress = z.object({
  [keys.address]: z.string().min(constraints.addressLength.min).max(constraints.addressLength.max)
});
const rawSchemaLocationWithCoordinates = z.object({
  [keys.latitude]: latSchema,
  [keys.longitude]: lngSchema
});
export const rawSchemaLocation = z.union([rawSchemaLocationWithAddress, rawSchemaLocationWithCoordinates], {
  errorMap: () => ({
    message: "A location must be specified either by providing an Address, or with coordinates by providing both Latitude and Longitude."
  })
});
export const portfolioExportRawSchema = rawSchemaBase.and(rawSchemaLocation);
export type PortfolioExportRawSchema = z.infer<typeof portfolioExportRawSchema>;

/**
 * `portfolioExportRawSchema` models location as a union - a row supplies either an
 * address or coordinates
 */
export type PortfolioExportRawRow = z.infer<typeof rawSchemaBase> & Partial<z.infer<typeof rawSchemaLocationWithAddress>> & Partial<z.infer<typeof rawSchemaLocationWithCoordinates>>;
const portfolioExportBatchEndpointFailure = z.object({
  type: z.literal("failure"),
  errors: z.array(z.string())
});
const portfolioExportBatchEndpointSuccess = z.object({
  type: z.literal("success")
});
export const portfolioExportBatchEndpointResponse = z.discriminatedUnion("type", [portfolioExportBatchEndpointFailure, portfolioExportBatchEndpointSuccess]);
export type PortfolioExportBatchEndpointResponse = z.infer<typeof portfolioExportBatchEndpointResponse>;